// TypeIcon.jsx

import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Services
import { updateNodeTypeIconKey } from 'services/nodetypes/NodeTypeMutationService';
import {
    getNodeTypesCacheForScope,
    makeBaseScopeKeyStrict,
    warmNodeTypesCache,
} from 'services/nodetypes/NodeTypesCacheService';

// CSS
import './TypeIcon.css';

// Components
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import { db } from 'firebaseConfig';

/**
 * TypeIcon
 *
 * Displays a node type icon and allows updating the iconKey via IconPicker.
 *
 * @param {string} typeKey - Node type key.
 * @param {number} size - Icon button size in pixels.
 * @param {string} iconKey - Optional controlled icon key.
 * @param {function} onChange - Optional controlled icon change handler.
 * @param {boolean} disabled - Optional disabled override.
 *
 * @returns {ReactNode} - TypeIcon.
 */
const TypeIcon = React.memo(({ typeKey, size = 46, iconKey, onChange, disabled = false }) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // Node Types Context
    const { getType } = useNodeTypes();

    // Base Context
    const { activeBase } = useBase();

    // ----- STATE ----- //

    const [saving, setSaving] = useState(false);

    const typeDef = typeKey ? getType(typeKey) : null;

    // We derive the active base key here since we need it for the base cache scope key and 
    // we want to ensure we have a stable value for the base cache.
    const activeBaseKey = typeof activeBase?.key === 'string'
        ? activeBase.key
        : '';

    const controlledIconKey = typeof iconKey === 'string' ? iconKey.trim() : '';
    const isControlled = typeof onChange === 'function';

    // We derive a scope key for the base cache based on the active base key. This allows 
    // us to have a separate cache for the base scope, which can be more up-to-date for the base 
    // type definition and can receive updates when the base type definition changes.
    const baseScopeKey = activeBaseKey
        ? makeBaseScopeKeyStrict(activeBaseKey)
        : null;

    // ----- MEMOS ----- //

    // We want to have a separate cache for the base scope since it will be more up-to-date 
    // for the base type definition, which allows us to show the latest icon and to have the most 
    // recent data when the user opens the IconPicker. This is especially important if the user has made 
    // recent changes to the type or if there are multiple users collaborating in the base, which could 
    // lead to frequent updates to the type definition.

    const baseCache = useMemo(
        () => (baseScopeKey ? getNodeTypesCacheForScope(baseScopeKey) : null),
        [baseScopeKey]
    );

    // Use useSyncExternalStore to subscribe to changes in the base cache's revision, which 
    // allows us to reactively update the icon if the base type definition changes due to external 
    // factors (e.g. another user updating the type).
    useSyncExternalStore(
        (cb) => (baseCache ? baseCache.onChange(cb) : () => {}),
        () => (baseCache ? baseCache.rev : ''),
        () => (baseCache ? baseCache.rev : '')
    );

    // Ensure the base cache is warmed for the active base to have the most
    // up-to-date data and to enable instant loading when the user opens the IconPicker.
    const baseTypeDef = !baseCache || !typeKey
        ? null
        : (baseCache.get(typeKey) || null);

    // Fall back to the base type definition if it exists since it will be more 
    // up-to-date than the global type definition, which may be stale if the base cache has 
    // been warmed and has received updates.
    const resolvedIconKey = useMemo(() => {
        if (isControlled) {
            return controlledIconKey || 'deployed_code';
        }

        const baseKeyValue = typeof baseTypeDef?.iconKey === 'string'
            ? baseTypeDef.iconKey.trim()
            : '';
        if (baseKeyValue) return baseKeyValue;

        const mergedKeyValue = typeof typeDef?.iconKey === 'string' ? typeDef.iconKey.trim() : '';
        return mergedKeyValue || 'deployed_code';
    }, [controlledIconKey, isControlled, typeDef?.iconKey, baseTypeDef?.iconKey]);

    // ----- EFFECTS ----- //

    // Warm the base cache on mount if we have an active base to ensure we have the latest data 
    // and to enable instant loading in the IconPicker.

    useEffect(() => {
        if (!baseScopeKey) return undefined;
        warmNodeTypesCache(db, { scopeKey: baseScopeKey, subscribe: true }).catch((error) => {
            console.error('[TypeIcon] Failed to warm base cache:', error);
        });
        return undefined;
    }, [baseScopeKey]);

    const isBaseType = isControlled || Boolean(baseTypeDef?.key);
    const isDisabled = disabled
        || saving
        || (!isControlled && (!typeKey || !activeBaseKey || !isBaseType));

    // ----- HANDLERS ----- //

    /**
     * Handles icon selection from the IconPicker.
     * 
     * @param {string} selectedKey - The key of the selected icon.
     * @param {object} options - Additional options.
     * @param {function} options.closePicker - Function to close the IconPicker popup.
     */

    const handleSelect = useCallback(async (selectedKey, { closePicker } = {}) => {
        if (saving) return;

        const nextKey = typeof selectedKey === 'string' ? selectedKey.trim() : '';

        if (isControlled) {
            onChange(nextKey);
            closePicker?.();
            return;
        }

        if (!typeKey || !activeBaseKey) return;

        setSaving(true);

        try {
            await updateNodeTypeIconKey({
                baseKey: activeBaseKey,
                typeKey,
                iconKey: nextKey,
            });
            closePicker?.();
        } catch (error) {
            console.error('Failed to update icon key:', error);
            alert(error?.message || 'Failed to update icon key.');
        } finally {
            setSaving(false);
        }
    }, [activeBaseKey, isControlled, onChange, saving, typeKey]);

    // ----- RENDER ----- //

    return (

        <IconPickerButton
          className="type-icon-container"
          backgroundColor="transparent"
          borderRadius={9}
          buttonSize={size}
          disabled={isDisabled}
          disabledTitle=""
          hoverBackgroundColor={theme.backgroundMuted}
          iconColor={theme.foreground}
          iconSize={30}
          popupHeight={400}
          popupWidth={400}
          saving={saving}
          selectedIconKey={resolvedIconKey}
          title="Choose icon"
          onSelect={handleSelect}
        />

    );
});

// Dev Tools
TypeIcon.displayName = 'TypeIcon';

export default TypeIcon;
